import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribeActiveContext } from '@kiao-voice/api-client'
import type { ActiveContextListener, VoiceUsageResponse } from '@kiao-voice/api-client'

/**
 * Minimum age a cached probe must reach before a window-focus event is
 * allowed to re-fetch it. The quota only changes when THIS user records (and
 * the recording flow `refresh()`es explicitly afterwards), so a focus-driven
 * re-probe is just a safety net for cross-device usage / plan changes — a
 * coarse cadence is plenty and keeps focus-thrash from bombarding the API.
 */
export const VOICE_QUOTA_REFRESH_STALE_MS = 60_000

/**
 * Unlimited (paid) users can never be `exhausted` — daily caps were dropped in
 * P3 — so re-probing them is pure waste. We still re-fetch on a much longer
 * cadence so a downgrade (paid → free) or a server-side config change
 * (sttMode / relay / default mode) is eventually picked up without a restart.
 */
export const VOICE_QUOTA_UNLIMITED_REFRESH_STALE_MS = 10 * 60_000

export interface VoiceQuotaState {
  usage: VoiceUsageResponse | null
  /** True only when a fresh probe says the user is out of quota. Defaults
   *  to false (fail-open) so a probe failure never blocks recording — the
   *  server gate still refuses a genuinely-exhausted user with a 402. */
  exhausted: boolean
  /**
   * A probe is in flight right now. Distinguishes "no usage YET" from "usage
   * unavailable": a context switch clears `usage` and immediately re-probes, so
   * a consumer that renders `usage === null` as an error would flash that error
   * on every workspace switch. Render a loading state while this is true and
   * only treat `usage === null` as a failure once it goes false.
   */
  isProbing: boolean
  refresh: () => Promise<void>
}

export interface UseVoiceQuotaOptions {
  enabled: boolean
  /**
   * Injected transport that fetches `GET /voice-usage/me`. Each app supplies
   * its own — desktop passes `() => getVoiceUsage(desktopApiClient)`, web passes
   * its token-based fetch — which is why the hook lives here and stays
   * platform-agnostic.
   *
   * MUST be referentially stable (wrap in `useCallback`); the focus /
   * visibility listeners and the mount probe key off it, so an unstable
   * fetcher would re-subscribe and re-probe on every render.
   */
  fetchUsage: () => Promise<VoiceUsageResponse>
  /**
   * Active-context subscription; defaults to the shared store's. Mirrors
   * `useOverview` / `useOrganizationIdentity`: every context-scoped reader
   * subscribes through the SAME store, so none of them can drift out of sync
   * with the org the server is actually acting in. Consumers normally omit this
   * — it exists as a test seam.
   *
   * Like `fetchUsage`, it MUST be referentially stable: it sits in the same
   * effect's dependency array, so an inline function would re-subscribe AND
   * re-run the mount probe on every render (one `/voice-usage/me` per render).
   * The default is a module-level function, hence stable.
   */
  subscribe?: (listener: ActiveContextListener) => () => void
  /**
   * Called with the raw cause whenever a probe fails, so a platform can
   * classify it (desktop turns a 401 / deactivated-403 into a sign-out). Purely
   * additive: the probe still fails open and still `console.warn`s, so a
   * consumer that omits this — web, whose api-client already self-heals a 401 —
   * behaves exactly as before.
   *
   * Only the NEWEST probe reports; a superseded one that settles late is
   * dropped by the same seq guard that protects `usage`, because it describes a
   * session state a newer request has already moved past (sign out, sign back
   * in, then the old 401 arrives).
   *
   * Unlike `fetchUsage` / `subscribe` this need NOT be referentially stable: it
   * is read through a ref and deliberately kept out of every dependency array.
   */
  onProbeError?: (cause: unknown) => void
}

/**
 * Shared voice-quota probe for desktop + web. Fetches `GET /voice-usage/me` so
 * the client can pre-empt a recording with the paywall when the user is out of
 * free voice quota — and learns the server-driven STT routing / onboarding
 * config that rides on the same payload.
 *
 * Event-driven (NOT a wall-clock poll): it fetches on mount, on window focus
 * once the cached value is stale, and on demand via `refresh()` (the recording
 * flow calls it after every transcription and after a 402/429). The previous
 * fixed 30 s `setInterval` ran 24/7 for every authenticated client regardless
 * of whether the user dictated at all — ~2,880 requests/day per idle client,
 * almost all from paid users who can never be exhausted. Tying the fetch to
 * actual usage instead of wall-clock time collapses that to a handful of
 * requests per active session. The server gate remains authoritative, so a
 * slightly-stale cached probe never lets an exhausted user through.
 * Disabled (and cleared) while unauthenticated.
 */
export function useVoiceQuota({
  enabled,
  fetchUsage,
  subscribe = subscribeActiveContext,
  onProbeError,
}: UseVoiceQuotaOptions): VoiceQuotaState {
  const [usage, setUsage] = useState<VoiceUsageResponse | null>(null)
  const [isProbing, setIsProbing] = useState(false)
  // Read the latest usage inside the focus handler without re-subscribing the
  // listener (which would otherwise re-run on every probe result).
  const usageRef = useRef<VoiceUsageResponse | null>(usage)
  useEffect(() => {
    usageRef.current = usage
  }, [usage])
  // Kept in a ref, and synced on every render rather than in an effect, so the
  // callback's identity never enters `probe`'s dependency array: an inline
  // callback would otherwise re-create `probe`, re-subscribe the focus /
  // visibility / context listeners and re-run the mount probe — one
  // `/voice-usage/me` per render.
  const onProbeErrorRef = useRef(onProbeError)
  onProbeErrorRef.current = onProbeError
  const lastFetchedAtRef = useRef(0)
  // Monotonic request id so a slow in-flight probe can never overwrite a newer
  // one's result (e.g. a focus re-probe racing a post-recording refresh).
  const requestSeqRef = useRef(0)

  const probe = useCallback(async () => {
    if (!enabled) {
      return
    }
    const seq = ++requestSeqRef.current
    setIsProbing(true)
    try {
      const next = await fetchUsage()
      if (seq === requestSeqRef.current) {
        setUsage(next)
        lastFetchedAtRef.current = Date.now()
      }
    } catch (cause) {
      // Fail open: the server gate is the real authority, so a flaky
      // probe must not block dictation. Log for visibility and keep the
      // last known usage rather than forcing `exhausted`.
      console.warn('Voice quota probe failed', cause)
      // Same seq guard as the success path: a superseded probe's failure
      // describes a session a newer request has already replaced, so reporting
      // it would sign out whoever is signed in now.
      if (seq === requestSeqRef.current) {
        onProbeErrorRef.current?.(cause)
      }
    } finally {
      // Only the newest probe may clear the flag — a superseded one resolving
      // late must not report "done" while its replacement is still in flight.
      if (seq === requestSeqRef.current) {
        setIsProbing(false)
      }
    }
  }, [enabled, fetchUsage])

  const refreshIfStale = useCallback(() => {
    if (!enabled) {
      return
    }
    const staleAfter = usageRef.current?.isUnlimited
      ? VOICE_QUOTA_UNLIMITED_REFRESH_STALE_MS
      : VOICE_QUOTA_REFRESH_STALE_MS
    if (Date.now() - lastFetchedAtRef.current >= staleAfter) {
      void probe()
    }
  }, [enabled, probe])

  useEffect(() => {
    if (!enabled) {
      // Invalidate any in-flight probe so a response that resolves AFTER
      // logout can't repopulate quota state for an unauthenticated user
      // (the seq guard in `probe` then drops it).
      requestSeqRef.current++
      setUsage(null)
      setIsProbing(false)
      lastFetchedAtRef.current = 0
      return
    }

    void probe()

    const onFocus = () => refreshIfStale()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshIfStale()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    // A workspace switch changes WHICH org's quota is authoritative, so the
    // cached probe must be re-read at once — NOT via `refreshIfStale`, whose
    // staleness window is about wall-clock drift and would leave a stale
    // `exhausted` in place for up to a full window: switching out of an
    // exhausted personal context would keep the desktop refusing to record on
    // the OLD context's verdict, and the reverse would show quota the server
    // then refuses with a 402. Every context-scoped reader (`useOverview`
    // included) subscribes here for exactly that reason.
    const unsubscribeContext = subscribe((_key, reason) => {
      // Invalidate any in-flight probe first: it was issued under the previous
      // header state, so letting it land would overwrite the result of the probe
      // fired below. Same seq-bump the `!enabled` branch above uses.
      requestSeqRef.current++

      // `adopt` is the one reason that KEEPS the cached usage. It means the
      // store is being seeded with the context the server had already resolved
      // for the preceding headerless requests, so the cached payload came from
      // that same context — stale-while-revalidate, with no window where
      // `usage` is null and the runtime config riding on it (sttMode,
      // taigiVoiceEnabled, maxSentenceDurationSecs, defaultEnhancementMode)
      // silently reverts to a client default. A dictation started inside that
      // window would drop out of realtime STT, lose Taigi routing and use the
      // wrong sentence cap.
      //
      // `set` and `clear` DO clear it. There the cached payload belongs to a
      // context that no longer applies, so it isn't merely stale — it is wrong,
      // and both `exhausted` and `isUnlimited` derive from it. Keeping it across
      // a re-probe that FAILS (offline, API blip) would leave the old context's
      // `exhausted: true` refusing to record: fail-CLOSED on someone else's
      // quota. Clearing preserves the hook's fail-open contract — unknown quota
      // never blocks, and the server gate still answers 402 when the user really
      // is out.
      if (reason !== 'adopt') {
        setUsage(null)
      }
      lastFetchedAtRef.current = 0

      // Every reason re-probes immediately, because the server-driven runtime
      // config rides on `usage` and no other event is guaranteed to restore it.
      // That matters most on the `active_context_forbidden` (403) recovery path:
      // a revoked seat recovering while still signed in, often from a global
      // hotkey in the BACKGROUND, with no focus/visibilitychange to ever trigger
      // `refreshIfStale`.
      //
      // Sign-out is NOT a source this closure ever sees — both platforms clear
      // with `{ notify: false }` (web's `clearStoredToken()`, desktop's
      // `persistSession(null)`), so no subscriber is notified on sign-out at
      // all. Every `'clear'` reaching here is a still-authorized, mid-session
      // 403 `active_context_forbidden` / 400 `invalid_active_context` recovery
      // fired while the session is alive, so this probe always carries a valid
      // token, with no 401 race to reason about.
      void probe()
    })

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      unsubscribeContext()
    }
  }, [enabled, probe, refreshIfStale, subscribe])

  return {
    usage,
    exhausted: usage?.exhausted ?? false,
    isProbing,
    refresh: probe,
  }
}
